import { useState, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../../context/ThemeContext";
import {
  useRoutesStore,
  type Route,
} from "../../../../store/admin/routesStore";
import { useRoutesList } from "../../../../features/routes/queries";
import { useDebounce } from "../../../../hooks/useDebounce";
import { qk } from "../../../../lib/queryKeys";
import { useCompaniesStore } from "../../../../store/admin/companiesStore";
import { useAlert } from "../../../../context/AlertContext";
import RoutesCard from "../Routes/RoutesCard";
import {
  RouteModal,
  RouteDetailsModal,
  RouteFilters,
  useRouteForm,
  type RouteFormValues,
} from "./routesComponents";
import { useDisableCRUD } from "../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
import PaginationControls from "../../../../components/base/PaginationControls";
import { DENSE_LIST, DENSE_ROW, ICON_BTN, STATUS_CHIP } from "../../shared";
import {
  getStatusBadgeClass,
  getStatusBadgeStyle,
  getStatusBadgeLabel,
} from "../../../../utils/getStatusEntity";

export default function RoutesSection() {
  const { t } = useTranslation("adminSections");
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedView, setSelectedView] = useState("list");
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Estados para el modal de crear/editar
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editRoute, setEditRoute] = useState<Route | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const { createRoute, updateRoute } = useRoutesStore();

  const debouncedSearch = useDebounce(searchTerm);
  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      status_id: statusFilter || undefined,
      search: debouncedSearch || undefined,
    }),
    [currentPage, itemsPerPage, statusFilter, debouncedSearch],
  );

  const routesQuery = useRoutesList(filterParams);
  const routes = routesQuery.data?.data ?? [];
  const meta = routesQuery.data?.meta;
  const totalPages = meta?.totalPages || 1;
  const loading = routesQuery.isLoading;
  const error = routesQuery.error ? "Error al cargar rutas" : null;

  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const invalidateRoutes = () => {
    queryClient.invalidateQueries({ queryKey: ["routes"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  const disableCRUD = useDisableCRUD();
  // Hook para el formulario de rutas
  const {
    form,
    routePoints,
    routeDistance,
    estimatedTime,
    hasRealChanges,
    handleMapPointsChange,
    handleCoordinateEdit,
    handleAddressEdit,
    handleAddPoint,
    handleRemovePoint,
    handleRouteCalculated,
    handleAxisCalculated,
    handleBufferChange,
    resetForm,
    prepareRouteData,
  } = useRouteForm({
    isEdit,
    editRoute,
  });

  // Reset page when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, debouncedSearch]);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  /*const routes = [
    {
        id: '1',
        name: 'Ruta Logística Industrial Zona Occidente',
        description: "Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quod. Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quod.",
        status: 'active',
        alternate_route: [
            {
                latitud: 10.35276,
                longitud: -67.039646
            },
            {
                latitud: 10.356248,
                longitud: -67.034142
            },
            {
                latitud: 10.356115,
                longitud: -67.037697
            } 
        ],
        createdAt: '22 de octubre de 2025 a las 1:44:31 p.m. UTC-4',
        geofences_id: [
            '83zWjwL05Ou6JyhZ0Sn1',
            '8P9lS22TixgyP8LaWL9l'
        ],
        principal_route: [
            {
                latitud: 10.352813,
                longitud: -67.039781
            },
            {
                latitud: 10.35702,
                longitud: -67.038879
            }
        ],
        updatedAt: '22 de octubre de 2025 a las 1:44:31 p.m. UTC-4'
    }
  ];*/

  const formatDuration = (minutes: number | undefined): string => {
    if (!minutes || minutes <= 0) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const handleDetailsClick = (route: Route) => {
    setSelectedRoute(route);
    setIsModalOpen(true);
  };

  // Handlers para el modal de crear/editar
  const handleAddNew = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setEditRoute(null);
    setLoadingSubmit(false);
    resetForm();
  };

  const handleEditRoute = useCallback(
    (route: Route) => {
      if (disableCRUD) return;
      setEditRoute(route);
      setIsEdit(true);
      setTimeout(() => {
        setShowAddModal(true);
      }, 0);
    },
    [disableCRUD],
  );

  // Submit handlers
  const handleCreateRouteSubmit = async (data: RouteFormValues) => {
    setLoadingSubmit(true);
    const companyId = localStorage.getItem("companyId");
    const routeData = {
      ...prepareRouteData(data),
      company_id: companyId || "",
    };

    const result = await createRoute(routeData);

    if (result.success) {
      showAlert({
        title: "Éxito",
        text: "Ruta creada correctamente",
        icon: "success",
      });
      handleCloseAddModal();
      invalidateRoutes();
    } else {
      showAlert({
        title: "Error",
        text: result.error || "Error al crear la ruta",
        icon: "error",
      });
    }
    setLoadingSubmit(false);
  };

  const handleUpdateRouteSubmit = async (data: RouteFormValues) => {
    if (!editRoute) return;
    setLoadingSubmit(true);

    const routeData = prepareRouteData(data);

    const result = await updateRoute(editRoute.id, routeData);

    if (result.success) {
      showAlert({
        title: "Éxito",
        text: "Ruta actualizada correctamente",
        icon: "success",
      });
      handleCloseAddModal();
      invalidateRoutes();
    } else {
      showAlert({
        title: "Error",
        text: result.error || "Error al actualizar la ruta",
        icon: "error",
      });
    }
    setLoadingSubmit(false);
  };

  const handleSubmit = isEdit
    ? handleUpdateRouteSubmit
    : handleCreateRouteSubmit;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 py-3 sm:px-6 sm:py-4`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
          <div className="min-w-0">
            <h1 className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}>
              Gestión de Rutas
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              Administra todas las rutas de la flota
            </p>
          </div>
          <CRUDTooltip className="self-start sm:self-auto flex-shrink-0">
            <button
              onClick={handleAddNew}
              disabled={disableCRUD}
              className="bg-primary-500 hover:bg-primary-400 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base"
            >
              <i className="ri-add-line"></i>
              <span>Nueva Ruta</span>
            </button>
          </CRUDTooltip>
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <RouteFilters
            status_id={statusFilter}
            searchQuery={searchTerm}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            onStatusChange={(value) => setStatusFilter(value || "")}
            onSearchChange={(e) => setSearchTerm(e.target.value)}
          />
          {/* View Toggle */}
          <div className="ml-auto flex shrink-0">
            <div
              className={`flex ${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-1`}
            >
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "grid"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("grid")}
              >
                <i className="ri-grid-fill"></i>
              </button>
              <button
                className={`px-3 py-1 rounded-md transition-colors ${
                  selectedView === "list"
                    ? "bg-primary-500 text-gray-900"
                    : `${themeClasses.buttonText} ${themeClasses.buttonHover}`
                }`}
                onClick={() => setSelectedView("list")}
              >
                <i className="ri-list-check"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-64">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {!loading && !error && routes.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64">
            <i className="ri-route-line text-6xl text-gray-400 mb-4"></i>
            <p className={`${themeClasses.textSecondary}`}>
              No hay rutas disponibles
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          routes.length > 0 &&
          (selectedView === "grid" ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {routes.map((route) => (
                  <RoutesCard
                    key={route.id}
                    route={route}
                    themeClasses={themeClasses}
                    onDetailsClick={handleDetailsClick}
                    onEditClick={handleEditRoute}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          ) : (
            <>
              <div className={DENSE_LIST}>
                {routes.map((route) => (
                  <div
                    key={route.id}
                    className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}
                  >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 bg-primary-500/10">
                          <i className="ri-route-line text-base text-primary-500"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3
                              className={`text-sm font-semibold ${themeClasses.text} truncate`}
                              title={route.name}
                            >
                              {route.name}
                            </h3>
                            <span
                              className={`${STATUS_CHIP} ${getStatusBadgeClass(route.status)}`}
                              style={getStatusBadgeStyle(route.status)}
                            >
                              {getStatusBadgeLabel(
                                route.status,
                                t,
                                t("units.status.unknown"),
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 min-w-0 overflow-hidden whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <i className={`ri-time-line text-xs ${themeClasses.textSecondary}`}></i>
                              <p className={`text-xs ${themeClasses.textSecondary}`}>
                                {formatDuration(route.principal_route_duration)}{" "}
                                restante
                              </p>
                            </div>

                            <div className="flex items-center gap-1">
                              <i className={`ri-truck-line text-xs ${themeClasses.textSecondary}`}></i>
                              <p className={`text-xs ${themeClasses.textSecondary}`}>
                                {route.vehicles_count || 0}
                              </p>
                            </div>

                            <div className="flex items-center gap-1">
                              <i className={`ri-map-pin-line text-xs ${themeClasses.textSecondary}`}></i>
                              <p className={`text-xs ${themeClasses.textSecondary}`}>
                                {route.principal_route?.length || 0} puntos
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end flex-shrink-0">
                        <div className="flex items-center gap-0.5">
                          <button
                            onClick={() => handleDetailsClick(route)}
                            aria-label="Ver detalles"
                            className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
                            title="Ver detalles"
                          >
                            <i className="ri-eye-line"></i>
                          </button>
                          <button
                            onClick={() => handleEditRoute(route)}
                            className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
                            aria-label="Editar ruta"
                            title="Editar ruta"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
                            aria-label="Archivo"
                            title="Archivo"
                          >
                            <i className="ri-file-line"></i>
                          </button>
                          <button
                            aria-label={t("general.delete", { ns: "common" })}
                            title={t("general.delete", { ns: "common" })}
                            className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          ))}
      </div>

      {/* Modal de Detalles */}
      <RouteDetailsModal
        isOpen={isModalOpen}
        route={selectedRoute}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRoute(null);
        }}
        onEdit={(route) => {
          setIsModalOpen(false);
          handleEditRoute(route);
        }}
      />

      {/* Modal de Creación/Edición */}
      <RouteModal
        isOpen={showAddModal}
        isEdit={isEdit}
        loadingSubmit={loadingSubmit}
        form={form}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        routePoints={routePoints}
        routeDistance={routeDistance}
        estimatedTime={estimatedTime}
        hasRealChanges={hasRealChanges}
        onCoordinateEdit={handleCoordinateEdit}
        onAddressEdit={handleAddressEdit}
        onAddPoint={handleAddPoint}
        onRemovePoint={handleRemovePoint}
        onMapPointsChange={handleMapPointsChange}
        onRouteCalculated={handleRouteCalculated}
        onAxisCalculated={handleAxisCalculated}
        onCorridorBufferChange={handleBufferChange}
        onSubmit={handleSubmit}
        onClose={handleCloseAddModal}
      />
    </div>
  );
}
